import { useState, useEffect, useRef } from "react";
import { Box, Typography, Button } from "@mui/material";
import { CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getPublicData } from "../../Services/getPublicData";
import { postData } from "../../Services/postData";
import { ClientModel } from "../../Utils/Models";
import { Subscription } from "../../Utils/Types";
import { Vocabulary } from "../../Utils/Vocabulary";
import { endpoints, localUrlEnum } from "../../Utils/UrlEnum";
import {
  disableTostify,
  PAYMENT_TYPES,
  SKIP_PAYMENT_IF_ZERO_AMOUNT,
} from "../../Utils/Constants";
import SubscriptionSelectionStep from "../PublicRegistration/SubscriptionSelectionStep";
import ClientFormStep from "../PublicRegistration/ClientFormStep";
import ExamCategoriesSelectionStep, {
  ExamCategory,
} from "./ExamCategoriesSelectionStep";
import GenericModal from "../GenericModal";
import styles from "./ClientRegistration.module.css";
import * as yup from "yup";
import { generatePaymentUrl } from "../../Utils/PaymentUtils";
import { isValidDate } from "../../Utils/Utils";

// Registration flow steps data - uses Vocabulary entries
const getRegistrationSteps = () => [
  {
    number: 1,
    title: Vocabulary.registrationStep1Title,
    description: Vocabulary.registrationStep1Description,
  },
  {
    number: 2,
    title: Vocabulary.registrationStep2Title,
    description: Vocabulary.registrationStep2Description,
  },
  {
    number: 3,
    title: Vocabulary.registrationStep3Title,
    description: Vocabulary.registrationStep3Description,
  },
  {
    number: 4,
    title: Vocabulary.registrationStep4Title,
    description: Vocabulary.registrationStep4Description,
  },
];

// Reusable Next Step Item Component
interface NextStepItemProps {
  number: number;
  title: string;
  description: string;
}

const NextStepItem = ({ number, title, description }: NextStepItemProps) => (
  <Box className={styles.nextStepItem}>
    <Box className={styles.nextStepNumber}>{number}</Box>
    <Typography className={styles.nextStepTitle}>{title}</Typography>
    <Typography className={styles.nextStepDescription}>
      {description}
    </Typography>
  </Box>
);

export default function ClientRegistrationPage() {
  const navigate = useNavigate();
  const clientFormRef = useRef<HTMLDivElement>(null);
  const examCategoriesRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState({
    subscriptions: [] as Subscription[],
    subscriptionsLoading: false,
    selectedSubscription: null as Subscription | null,
    examCategories: [] as ExamCategory[],
    examCategoriesLoading: false,
    selectedExamCategory: null as ExamCategory | null,
    clientData: new ClientModel(),
    loading: false,
    agreeTerms: false,
    agreePrivacy: false,
    showZeroAmountModal: false,
  });

  const {
    subscriptions,
    subscriptionsLoading,
    selectedSubscription,
    examCategories,
    examCategoriesLoading,
    selectedExamCategory,
    clientData,
    loading,
    agreeTerms,
    agreePrivacy,
    showZeroAmountModal,
  } = state;

  const clientValidationSchema = yup.object().shape({
    firstName: yup.string().required(Vocabulary.requiredField),
    lastName: yup.string().required(Vocabulary.requiredField),
    email: yup
      .string()
      .email(Vocabulary.emailValidation)
      .required(Vocabulary.requiredField),
    phone: yup
      .string()
      .required(Vocabulary.requiredField)
      .matches(/^(\+4|4|0)\d{8,9}$/, Vocabulary.phoneValidation),
    CNP: yup.string().optional(),
    ciIssuedDate: yup
      .string()
      .test(
        "valid-date",
        "Invalid date format",
        (value) => !value || isValidDate(value)
      )
      .optional(),
    address: yup.object().shape({
      country: yup.string().required(Vocabulary.requiredField),
      county: yup.string().required(Vocabulary.requiredField),
      city: yup.string().required(Vocabulary.requiredField),
    }),
  });

  // Fetch subscriptions on component mount
  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        setState((prev) => ({ ...prev, subscriptionsLoading: true }));
        const response = (await getPublicData(endpoints.subscriptions)) as any;

        if (response && !response.error) {
          const data = (response.data || response || []).map(
            (sub: any, index: number) => ({
              ...sub,
              isPopular: index === 1,
            })
          );
          setState((prev) => ({
            ...prev,
            subscriptions: data,
            subscriptionsLoading: false,
          }));
        }
      } catch (error) {
        console.error("Error fetching subscriptions:", error);
        setState((prev) => ({
          ...prev,
          subscriptionsLoading: false,
        }));
      }
    };

    fetchSubscriptions();
  }, []);

  // Fetch exam categories when subscription is selected
  useEffect(() => {
    if (selectedSubscription) {
      const fetchExamCategories = async () => {
        try {
          setState((prev) => ({ ...prev, examCategoriesLoading: true }));
          const response = await getPublicData(
            `${endpoints.examSets.activeWithQuestionDistinct}`
          );

          if (response) {
            const data = (response?.data || response || []).map(
              (category: any) => ({
                _id: category._id,
                name: category.name,
              })
            );
            setState((prev) => ({
              ...prev,
              examCategories: data,
              examCategoriesLoading: false,
              selectedExamCategory: null,
            }));
          }
        } catch (error) {
          console.error("Error fetching exam categories:", error);
          setState((prev) => ({
            ...prev,
            examCategoriesLoading: false,
          }));
        }
      };

      fetchExamCategories();
    }
  }, [selectedSubscription]);

  // Scroll to exam categories when subscription is selected
  useEffect(() => {
    if (selectedSubscription && examCategories.length > 0) {
      setTimeout(() => {
        examCategoriesRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [selectedSubscription, examCategories]);

  // Scroll to client form when exam category is selected
  useEffect(() => {
    if (selectedExamCategory) {
      setTimeout(() => {
        clientFormRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [selectedExamCategory]);

  const handleSubscriptionSelect = (subscription: Subscription) => {
    setState((prev) => ({
      ...prev,
      selectedSubscription: subscription,
    }));
  };

  const handleExamCategorySelect = (category: ExamCategory) => {
    setState((prev) => ({
      ...prev,
      selectedExamCategory: category,
    }));
  };

  const handleClientDataChange = (field: keyof ClientModel, value: any) => {
    setState((prev) => ({
      ...prev,
      clientData: {
        ...prev.clientData,
        [field]: value,
      },
    }));
  };

  /**
   * Handle scanned ID data and update client data accordingly
   * @param {any} data - Scanned ID data
   */
  const handleScanComplete = (data: any) => {
    const processedData = data.result;

    // Extract and validate the issued and expiry dates from validity field
    let ciIssuedDate = "";
    let ciExpirationDate = "";
    if (processedData?.validity) {
      const dateParts = processedData.validity
        .split("-")
        .map((date: string) => date.trim());
      // Only set each date if it's valid (not just a year)
      if (dateParts[0] && isValidDate(dateParts[0])) {
        ciIssuedDate = dateParts[0];
      }
      if (dateParts[1] && isValidDate(dateParts[1])) {
        ciExpirationDate = dateParts[1];
      }
    }

    setState((prev) => ({
      ...prev,
      clientData: {
        ...prev.clientData,
        CNP: processedData?.CNP || "",
        firstName: processedData?.firstName || "",
        lastName: processedData?.lastName || "",
        gender: processedData?.sex || "",
        ciSerialNumber: processedData?.series || "",
        ciNumber: processedData?.number || "",
        ciIssuedBy: processedData?.issuingAuthority || "",
        ciIssuedDate: ciIssuedDate,
        ciExpirationDate: ciExpirationDate,
        ci: processedData?.path || "",
        subscriptionId: processedData?.subscriptionId || null,
      },
    }));
  };

  /**
   * Handles client registration form submission.
   * Calls the API to create a new client registration using the provided data.
   * If the subscription price is 0 and SKIP_PAYMENT_IF_ZERO_AMOUNT is enabled:
   *   - Shows a modal with "Credentials were sent to mail" message
   * Otherwise:
   *   - Generates a payment URL using the reusable utility
   */
  const handleSubmit = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true }));

      const registrationData = {
        ...clientData,
        deletedAt: new Date(),
        agreeTerms,
        agreePrivacy,
      };

      await postData(endpoints.client.main, registrationData).then(
        async (response) => {
          if (response && response.data) {
            const client = response.data;

            // Check if we should skip payment due to zero amount
            if (SKIP_PAYMENT_IF_ZERO_AMOUNT && !selectedSubscription?.price) {
              // Activate subscription immediately (no payment needed)
              await postData(
                endpoints.client.activateSubscription + disableTostify,
                {
                  clientId: client._id,
                  subscriptionId: selectedSubscription?._id || "",
                  ...(selectedExamCategory?._id
                    ? { productId: selectedExamCategory._id }
                    : { examCategory: selectedExamCategory?.name }),
                }
              );

              // Generate the client account
              await postData(
                endpoints.client.generateUserAccount + disableTostify,
                {
                  clientId: client?._id,
                }
              );

              // Show modal instead of redirecting to payment
              setState((prev) => ({ ...prev, showZeroAmountModal: true }));
            } else {
              // Generate payment URL - server-side confirmOnlinePayment activates
              // the subscription after payment (see confirm-payment endpoint)
              generatePaymentUrl({
                clientId: client._id,
                clientData,
                selectedSubscription,
                purpose: PAYMENT_TYPES.SUBSCRIPTION_CREATED,
                metadata: {
                  subscriptionId: selectedSubscription?._id,
                  ...(selectedExamCategory?._id
                    ? { productId: selectedExamCategory._id }
                    : { examCategory: selectedExamCategory?.name }),
                },
              });
            }
          }
        }
      );
    } catch (error) {
      console.error("Error creating registration:", error);
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const isFormValid = () => {
    try {
      clientValidationSchema.validateSync(clientData);
      return agreeTerms && agreePrivacy && selectedExamCategory;
    } catch {
      return false;
    }
  };

  return (
    <div className={styles.pageContainer}>
      <Box className={styles.contentWrapper}>
        {/* Header */}
        <Box className={styles.header}>
          <Typography className={styles.headerTitle}>
            {Vocabulary.registrationTitle}
          </Typography>
          <Typography className={styles.headerSubtitle}>
            {Vocabulary.registrationDescription}
          </Typography>
        </Box>

        {/* Subscription Plans Section */}
        <Box className={styles.plansSection}>
          <Box className={styles.plansGrid}>
            <SubscriptionSelectionStep
              subscriptions={subscriptions}
              selectedSubscription={selectedSubscription}
              onSubscriptionSelect={handleSubscriptionSelect}
              loading={subscriptionsLoading}
            />
          </Box>
        </Box>

        {/* Exam Categories Section - Show only after subscription is selected */}
        {selectedSubscription && (
          <Box ref={examCategoriesRef}>
            <Box className={styles.formHeader}>
              <Typography variant="h4" className={styles.formTitle}>
                {Vocabulary.selectExamCategory}
              </Typography>
              <Typography variant="body1" className={styles.formSubtitle}>
                {Vocabulary.selectExamCategoryDescription}
              </Typography>
            </Box>

            <Box className={styles.plansGrid}>
              <ExamCategoriesSelectionStep
                categories={examCategories}
                selectedCategory={selectedExamCategory}
                onCategorySelect={handleExamCategorySelect}
                loading={examCategoriesLoading}
              />
            </Box>
          </Box>
        )}

        {/* Form Section - Show only after exam category is selected */}
        {selectedExamCategory && (
          <Box ref={clientFormRef}>
            <Box className={styles.formHeader}>
              <Typography variant="h4" className={styles.formTitle}>
                {Vocabulary.personalInfo}
              </Typography>
              <Typography variant="body1" className={styles.formSubtitle}>
                {Vocabulary.personalInfoStepDescription}
              </Typography>
            </Box>

            <ClientFormStep
              clientData={clientData}
              agreeTerms={agreeTerms}
              agreePrivacy={agreePrivacy}
              onClientDataChange={handleClientDataChange}
              onAgreeTermsChange={(value) =>
                setState((prev) => ({ ...prev, agreeTerms: value }))
              }
              onAgreePrivacyChange={(value) =>
                setState((prev) => ({ ...prev, agreePrivacy: value }))
              }
              onScanComplete={handleScanComplete}
              showScanButton={false}
              showCNP={false}
            />

            <Box className={styles.buttonContainer}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={!isFormValid() || loading}
                className={styles.submitButton}
              >
                {loading
                  ? Vocabulary.isProcessing
                  : SKIP_PAYMENT_IF_ZERO_AMOUNT && !selectedSubscription?.price
                  ? Vocabulary.createAccount
                  : Vocabulary.finishAndPay}
              </Button>
            </Box>
          </Box>
        )}

        {/* Next Steps Section - Show after subscription selected */}
        {selectedSubscription && (
          <Box className={styles.nextStepsSection}>
            <Typography variant="h4" className={styles.nextStepsTitle}>
              {Vocabulary.whatHappensNext}
            </Typography>
            <Box className={styles.nextStepsGrid}>
              {getRegistrationSteps().map((step) => (
                <NextStepItem
                  key={step.number}
                  number={step.number}
                  title={step.title}
                  description={step.description}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Empty State - No subscription selected */}
        {!selectedSubscription && !subscriptionsLoading && (
          <Box className={styles.emptyState}>
            <Typography className={styles.emptyStateTitle}>
              {Vocabulary.selectPlanToContinue}
            </Typography>
            <Typography className={styles.emptyStateText}>
              {Vocabulary.afterSelectingPlanPersonalInfo}
            </Typography>
          </Box>
        )}

        {/* Empty State - Subscription selected but no exam category selected */}
        {selectedSubscription &&
          !selectedExamCategory &&
          !examCategoriesLoading && (
            <Box className={styles.emptyState}>
              <Typography className={styles.emptyStateTitle}>
                {Vocabulary.selectExamCategoryToContinue}
              </Typography>
              <Typography className={styles.emptyStateText}>
                {Vocabulary.afterSelectingCategoryPersonalInfo}
              </Typography>
            </Box>
          )}
      </Box>

      {/* Modal for zero amount registration */}
      <GenericModal
        open={showZeroAmountModal}
        title=""
        onClose={() => {
          setState((prev) => ({ ...prev, showZeroAmountModal: false }));
          navigate(localUrlEnum.login);
        }}
        actions={
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={() => {
              setState((prev) => ({ ...prev, showZeroAmountModal: false }));
              navigate(localUrlEnum.login);
            }}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              textTransform: "none",
              fontSize: "1rem",
            }}
          >
            {Vocabulary.login}
          </Button>
        }
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "40px 32px 32px",
            gap: 2.5,
          }}
        >
          {/* Animated Success Icon */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 88,
              height: 88,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              boxShadow: "0 12px 32px rgba(102, 126, 234, 0.25)",
              animation: "scaleIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)",
              "@keyframes scaleIn": {
                "0%": {
                  transform: "scale(0.3)",
                  opacity: 0,
                },
                "100%": {
                  transform: "scale(1)",
                  opacity: 1,
                },
              },
            }}
          >
            <CheckCircleIcon
              sx={{
                fontSize: 64,
                color: "white",
              }}
            />
          </Box>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              textAlign: "center",
              color: "#1a1a1a",
              fontSize: "1.3rem",
              marginTop: 0.5,
            }}
          >
            {Vocabulary.operationCompletedSuccessfully}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              color: "#555",
              lineHeight: 1.7,
              fontSize: "0.95rem",
              maxWidth: "280px",
              fontWeight: 400,
            }}
          >
            {Vocabulary.credentialsEmailSent}
          </Typography>
        </Box>
      </GenericModal>
    </div>
  );
}
